/**
 * Каталог макетов v2 — константы и хелперы.
 */

import type { BadgeVariant } from '../../components/ui/Badge'
import type { DesignV2, FiltersV2, TagColor } from './types'
import {
  LEGAL_STATUSES as SHARED_LEGAL_STATUSES,
  legalStatusDotClass,
} from '../../shared/legalStatus'

// ── Состояние макета (производное поле design_state, 2026-08-06) ────
//
// Не путать с конвейером: pipeline_status — это прогресс производства, его
// двигает оркестратор. Состояние сворачивает 15 стадий конвейера, наличие
// товаров, факт живой карточки на МП и правовой статус в один ответ на вопрос
// «что с макетом сейчас». Считается на бэкенде, руками не правится.
//
// Человек ставит только два значения — «Приостановлен» и «Архив» (см.
// LIFECYCLE_FLAG_OPTIONS): их невозможно вывести из данных.

export type DesignState =
  | 'draft'
  | 'in_design'
  | 'on_acceptance'
  | 'in_production'
  | 'ready_to_publish'
  | 'selling'
  | 'marked_no_card'
  | 'error'
  | 'paused'
  | 'archived'
  | 'forbidden'

export const DESIGN_STATES: Array<{
  value: DesignState
  label: string
  variant: BadgeVariant
}> = [
  { value: 'draft', label: 'Черновик', variant: 'gray' },
  { value: 'in_design', label: 'В работе у дизайнера', variant: 'gray' },
  { value: 'on_acceptance', label: 'На приёмке', variant: 'yellow' },
  { value: 'in_production', label: 'В производстве', variant: 'blue' },
  // Выкладка на площадки — из «Черновиков ПИМ» (решение владельца 04.10.2026),
  // поэтому подпись называет, чего макет ждёт, а не «публикацию» в макете.
  { value: 'ready_to_publish', label: 'Ждёт выкладки', variant: 'blue' },
  // Только подтверждённый факт: живая карточка в зеркале каталога WB/OZON.
  // Продаж и остатков PIM не проверяет, поэтому «на площадке», а не «продаётся».
  { value: 'selling', label: 'Есть на площадке', variant: 'green' },
  // Отметка конвейера `selling` без живой карточки — ничем не подтверждена.
  { value: 'marked_no_card', label: 'Отметка без карточки', variant: 'gray' },
  { value: 'error', label: 'Ошибка', variant: 'red' },
  { value: 'paused', label: 'Приостановлен', variant: 'yellow' },
  { value: 'archived', label: 'Архив', variant: 'gray' },
  { value: 'forbidden', label: 'Запрещён', variant: 'red' },
]

/** Ручной оверрайд состояния — единственное, что выбирает человек. */
export const LIFECYCLE_FLAG_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Автоматически' },
  { value: 'paused', label: 'Приостановлен' },
  { value: 'archived', label: 'Архив' },
]

export const LEGAL_STATUSES = [...SHARED_LEGAL_STATUSES]

export const SEO_STATUSES: Array<{ value: string; label: string }> = [
  { value: 'empty', label: 'Не заполнено' },
  { value: 'draft', label: 'Заполнено ИИ' },
  { value: 'ai_filled', label: 'Заполнено ИИ' },
  { value: 'ready', label: 'Готово' },
  { value: 'approved', label: 'Утверждено' },
]

export const AGE_OPTIONS = ['Малыш', 'Ребёнок', 'Подросток', 'Взрослый (18+)', 'Пожилой']

const DESIGN_STATE_BY_VALUE = new Map(DESIGN_STATES.map((state) => [state.value, state]))

export function designStateVariant(state?: string | null): BadgeVariant {
  return DESIGN_STATE_BY_VALUE.get(state as DesignState)?.variant ?? 'gray'
}

/** Слаг состояния → русская подпись. Неизвестное значение показываем как есть. */
export function designStateLabel(state?: string | null): string {
  if (!state) return 'Черновик'
  return DESIGN_STATE_BY_VALUE.get(state as DesignState)?.label ?? state
}

export function legalDotClass(status?: string | null): string {
  return legalStatusDotClass(status)
}

/** pipeline_status → подпись + цвет точки для колонки «Конвейер» */
export function pipelineStageInfo(status?: string | null): { label: string; dot: string } | null {
  if (!status) return null
  switch (status) {
    case 'on_acceptance':
      return { label: 'На приёмке', dot: 'bg-yellow-500' }
    case 'products_created':
    case 'awaiting_media':
      return { label: 'Новые товары', dot: 'bg-blue-500' }
    case 'generating':
    case 'media_ready':
      return { label: 'Генерация', dot: 'bg-purple-500' }
    case 'on_approval':
      return { label: 'Черновики готовы', dot: 'bg-blue-500' }
    case 'publishing':
      return { label: 'Публикация', dot: 'bg-blue-500' }
    case 'awaiting_sales_start':
      return { label: 'Коммерция только в PIM', dot: 'bg-yellow-500' }
    case 'selling':
      return { label: 'Историческая отметка PIM', dot: 'bg-gray-400' }
    case 'error':
      return { label: 'Ошибка', dot: 'bg-red-500' }
    case 'returned_to_designer':
      return { label: 'Возврат', dot: 'bg-red-500' }
    case 'imported':
      return { label: 'Импортирован', dot: 'bg-gray-400' }
    case 'draft':
      return { label: 'Черновик', dot: 'bg-gray-400' }
    default:
      return { label: status, dot: 'bg-gray-400' }
  }
}

// ── Теги ────────────────────────────────────────────────────────────
// Палитра переехала в components/catalog/tags: словарь тегов у макетов и
// товаров общий, значит и цвета должны быть одни на оба каталога.

export {
  TAG_CHIP_CLASSES,
  TAG_DOT_CLASSES,
  TAG_COLORS,
  tagChipClass,
  tagDotClass,
  nextTagColor,
} from '../../components/catalog/tags'

// ── Форматирование ──────────────────────────────────────────────────

export function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
}

export function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU')
}

/** Дней в очереди приёмки от created_at */
export function daysInQueue(createdAt?: string | null): number | null {
  if (!createdAt) return null
  const d = new Date(createdAt)
  if (Number.isNaN(d.getTime())) return null
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000))
}

/** 1 день / 2 дня / 5 дней */
export function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return forms[2]
  if (last > 1 && last < 5) return forms[1]
  if (last === 1) return forms[0]
  return forms[2]
}

// ── Техконтроль ─────────────────────────────────────────────────────

export type TechChipState = 'ok' | 'fail' | 'na'

export interface TechChip {
  label: string
  state: TechChipState
  title?: string
}

/**
 * tech_check backend: { passed, min_dpi, required:{psd,pdf,jpg}, jpg:{dpi_x,dpi_y}, issues }
 * → 4 чипа PDF · JPG 96 · JPG 300 · Распол. (см. pipeline/adapters.deriveTechControl)
 */
export function techChips(techCheck: unknown): TechChip[] {
  const labels = ['PDF', 'JPG 96', 'JPG 300', 'Распол.']
  if (!techCheck || typeof techCheck !== 'object') {
    return labels.map((label) => ({ label, state: 'na' as TechChipState }))
  }
  const tc = techCheck as Record<string, any>
  const formats = (tc.formats && typeof tc.formats === 'object' ? tc.formats : {}) as Record<
    string,
    unknown
  >
  const explicit = (key: string): TechChipState | undefined => {
    if (!(key in formats)) return undefined
    const v = formats[key]
    return v === true ? 'ok' : v === false ? 'fail' : 'na'
  }
  const minDpi = Number(tc.min_dpi) || 300
  const hasJpg = Boolean(tc.jpg && typeof tc.jpg === 'object')
  const jpgDpi = hasJpg ? Math.max(Number(tc.jpg.dpi_x) || 0, Number(tc.jpg.dpi_y) || 0) : 0
  const issues = Array.isArray(tc.issues) ? tc.issues.join('; ') : undefined

  const pdf: TechChipState =
    explicit('pdf') ?? (tc.required ? (tc.required.pdf ? 'ok' : 'fail') : 'na')
  const jpg96: TechChipState =
    explicit('jpg96') ?? (hasJpg ? (jpgDpi > 0 && jpgDpi < 200 ? 'ok' : 'fail') : 'na')
  const jpg300: TechChipState =
    explicit('jpg300') ?? (hasJpg ? (jpgDpi >= minDpi ? 'ok' : 'fail') : 'na')
  const placement: TechChipState = explicit('placement') ?? 'na'
  return [
    { label: 'PDF', state: pdf, title: pdf === 'fail' ? issues : undefined },
    { label: 'JPG 96', state: jpg96, title: jpg96 === 'fail' ? issues : undefined },
    { label: 'JPG 300', state: jpg300, title: jpg300 === 'fail' ? issues : undefined },
    { label: 'Распол.', state: placement },
  ]
}

/** Весь техконтроль зелёный (для «Принять все проверенные») */
export function techAllOk(techCheck: unknown): boolean {
  const chips = techChips(techCheck)
  // «Распол.» пока не считается backend'ом — na не блокирует
  return chips.every((c) => c.state === 'ok' || (c.label === 'Распол.' && c.state === 'na'))
}

/** Количество активных фильтров панели «Все фильтры» (для бейджа) */
export function countPanelFilters(f: FiltersV2): number {
  let n = 0
  if (f.gender.length) n++
  if (f.age.length) n++
  if (f.seasonal) n++
  if (f.legal.length) n++
  if (f.occasion.length) n++
  if (f.seo.length) n++
  if (f.kit) n++
  if (f.preview) n++
  return n
}

export function hasAnyFilters(f: FiltersV2): boolean {
  return Boolean(
    f.search ||
    f.status.length ||
    f.type.length ||
    f.category.length ||
    f.brand.length ||
    f.themes.length ||
    f.dateFrom ||
    f.dateTo ||
    f.tagIds.length ||
    f.noTags ||
    countPanelFilters(f) > 0,
  )
}

// ── localStorage ────────────────────────────────────────────────────

export function loadLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function saveLS(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // квота/приватный режим — молча пропускаем
  }
}

export const LS_KEYS = {
  view: 'designsV2.view',
  mode: 'designsV2.mode',
  grouping: 'designsV2.grouping',
  acceptanceMode: 'designsV2.acceptanceMode',
  acceptanceGrouping: 'designsV2.acceptanceGrouping',
  savedViews: 'designsV2.savedViews',
  pageSize: 'designsV2.pageSize',
}

/** Название макета для списков */
export function designTitle(d: DesignV2): string {
  return (
    d.design_name ||
    [
      d.brand_ru,
      d.type,
      d.name_on_image,
      d.age_on_image ? `${d.age_on_image} ${d.age_on_image_unit || ''}`.trim() : '',
    ]
      .filter(Boolean)
      .join(', ') ||
    d.image_text ||
    `Макет ${d.design_code}`
  )
}
